import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Badge,
    Card,
    Col,
    Container,
    ProgressBar,
    Row,
} from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { FaEdit, FaEye } from 'react-icons/fa';

import SmartTopTool from '../../../components/Smart/SmartTopTool';
import SmartPagination from '../../../components/Smart/SmartPagination';
import SmartTable from '../../../components/Smart/SmartTable';
import API from '../../../api/axios';
import { useFormat } from '../../../components/FormatContext';

const SEARCH_TYPE = [
    {
        key: 'description',
        label: 'Description',
    },
];

const DATE_OPTIONS = [
    'createdAt',
    'validStart',
    'validEnd',
    'rentStart',
    'rentEnd',
];

const FILTER_OPTIONS = {
    type: [
        'PERCENTAGE',
        'FIXED_AMOUNT',
    ],
    onlyValidNow: [
        'true',
        'false',
    ],
    usageState: [
        'UNUSED',
        'IN_USE',
        'EXHAUSTED',
    ],
};

const DEFAULT_FILTERS = {
    dateFilters: [],
    type: [],
    onlyValidNow: [],
    usageState: [],
};

const renderType = (item) => {
    const isPercent =
        item.type === 'PERCENTAGE';

    return (
        <Badge
            bg={
                isPercent
                    ? 'success'
                    : 'primary'
            }
        >
            {isPercent
                ? 'Percentage'
                : 'Fixed Amount'}
        </Badge>
    );
};

const renderUsage = (item) => {
    const used =
        Number(item.used || 0);

    const userLimit =
        Number(item.userLimit || 0);

    const percent =
        userLimit > 0
            ? Math.min(
                100,
                (used / userLimit) * 100,
            )
            : 0;

    return (
        <div
            style={{
                minWidth: 150,
            }}
        >
            <div className="small mb-1">
                {used} / {userLimit}
            </div>

            <ProgressBar
                now={percent}
                variant={
                    item.isExhausted
                        ? 'danger'
                        : 'info'
                }
                style={{
                    height: 6,
                }}
            />
        </div>
    );
};

const renderValidity = (item) => {
    const now =
        new Date();

    const start =
        new Date(
            item.validStartDate,
        );

    const end =
        new Date(
            item.validEndDate,
        );

    if (item.isExhausted) {
        return (
            <Badge bg="danger">
                Exhausted
            </Badge>
        );
    }

    if (now < start) {
        return (
            <Badge bg="secondary">
                Not Started
            </Badge>
        );
    }

    if (now > end) {
        return (
            <Badge bg="dark">
                Expired
            </Badge>
        );
    }

    return (
        <Badge bg="success">
            Valid
        </Badge>
    );
};

const PromoCodesPage = () => {
    const {
        baseFormatters,
    } = useFormat();

    const [page, setPage] =
        useState({
            page_number: 1,
            total_pages: 1,
        });

    const [meta, setMeta] =
        useState({
            total: 0,
            page: 1,
            pageSize: 10,
            totalPages: 1,
        });

    const [rows, setRows] =
        useState([]);

    const [loading, setLoading] =
        useState(false);

    const [error, setError] =
        useState('');

    const [
        searchConditions,
        setSearchConditions,
    ] = useState([]);

    const [filters, setFilters] =
        useState(DEFAULT_FILTERS);

    const fetchPromoCodes =
        useCallback(async () => {
            try {
                setLoading(true);
                setError('');

                const params =
                    new URLSearchParams();

                params.set(
                    'page',
                    String(
                        page.page_number ||
                        1,
                    ),
                );

                params.set(
                    'pageSize',
                    String(
                        meta.pageSize ||
                        10,
                    ),
                );

                searchConditions.forEach(
                    ({
                        type,
                        value,
                    }) => {
                        if (
                            value == null ||
                            String(
                                value,
                            ).trim() === ''
                        ) {
                            return;
                        }

                        const normalizedValue =
                            String(
                                value,
                            ).trim();

                        if (
                            type ===
                            'description'
                        ) {
                            params.set(
                                'description',
                                normalizedValue,
                            );
                        }
                    },
                );

                filters.type.forEach(
                    (type) => {
                        params.append(
                            'type',
                            type,
                        );
                    },
                );

                if (
                    filters
                        .onlyValidNow?.[0]
                ) {
                    params.set(
                        'onlyValidNow',
                        filters
                            .onlyValidNow[0],
                    );
                }

                if (
                    filters
                        .usageState?.[0]
                ) {
                    params.set(
                        'usageState',
                        filters
                            .usageState[0],
                    );
                }

                /*
                 * 当前原代码虽然给 SmartTopTool
                 * 传了 dateOptions/dateFilters，
                 * 但没有把 dateFilters 映射到 API。
                 *
                 * 暂时保持原业务逻辑，不猜后端 DTO。
                 */

                const queryString =
                    params.toString();

                const url =
                    queryString
                        ? `/codes/promo?${queryString}`
                        : '/codes/promo';

                const { data } =
                    await API.get(url);

                setRows(
                    data?.data || [],
                );

                const nextMeta =
                    data?.meta || {
                        total: 0,
                        page: 1,
                        pageSize:
                            meta.pageSize ||
                            10,
                        totalPages: 1,
                    };

                setMeta(nextMeta);

                const nextPage =
                    nextMeta.page || 1;

                const nextTotalPages =
                    nextMeta.totalPages ||
                    1;

                setPage((prev) => {
                    if (
                        prev.page_number ===
                        nextPage &&
                        prev.total_pages ===
                        nextTotalPages
                    ) {
                        return prev;
                    }

                    return {
                        ...prev,
                        page_number:
                            nextPage,
                        total_pages:
                            nextTotalPages,
                    };
                });
            } catch (e) {
                setError(
                    e?.response?.data
                        ?.message ||
                    e?.message ||
                    'Load failed',
                );
            } finally {
                setLoading(false);
            }
        }, [
            page.page_number,
            meta.pageSize,
            searchConditions,
            filters.type,
            filters.onlyValidNow,
            filters.usageState,
        ]);

    const handleSetSearchConditions =
        useCallback(
            (valueOrUpdater) => {
                setSearchConditions(
                    (prev) =>
                        typeof valueOrUpdater ===
                            'function'
                            ? valueOrUpdater(
                                prev,
                            )
                            : valueOrUpdater,
                );

                setPage((prev) => {
                    if (
                        prev.page_number ===
                        1
                    ) {
                        return prev;
                    }

                    return {
                        ...prev,
                        page_number: 1,
                    };
                });
            },
            [],
        );

    const handleSetFilters =
        useCallback(
            (valueOrUpdater) => {
                setFilters(
                    (prev) =>
                        typeof valueOrUpdater ===
                            'function'
                            ? valueOrUpdater(
                                prev,
                            )
                            : valueOrUpdater,
                );

                setPage((prev) => {
                    if (
                        prev.page_number ===
                        1
                    ) {
                        return prev;
                    }

                    return {
                        ...prev,
                        page_number: 1,
                    };
                });
            },
            [],
        );

    useEffect(() => {
        fetchPromoCodes();
    }, [fetchPromoCodes]);

    const columns = useMemo(
        () => {
            const formatDate = (date) =>
                baseFormatters.date.numeric.format(
                    new Date(date),
                );

            const renderDiscount = (
                item,
            ) => {
                if (
                    item.type ===
                    'PERCENTAGE'
                ) {
                    return `${item.discount}%`;
                }

                if (
                    baseFormatters?.currency
                ) {
                    return baseFormatters.currency.format(
                        item.discount,
                    );
                }

                return item.discount;
            };

            return [
                {
                    key: 'code',
                    label: 'Code',
                    render: (item) => (
                        <span className="fw-semibold">
                            {item.code}
                        </span>
                    ),
                },
                {
                    key: 'type',
                    label: 'Type',
                    render:
                        renderType,
                },
                {
                    key: 'discount',
                    label: 'Discount',
                    render:
                        renderDiscount,
                },
                {
                    key: 'usage',
                    label: 'Usage',
                    render:
                        renderUsage,
                },
                {
                    key: 'status',
                    label: 'Status',
                    render:
                        renderValidity,
                },
                {
                    key: 'validWindow',
                    label:
                        'Valid Period',
                    render: (item) => (
                        <small className="text-muted">
                            {formatDate(
                                item.validStartDate,
                            )}{' '}
                            –{' '}
                            {formatDate(
                                item.validEndDate,
                            )}
                        </small>
                    ),
                },
                {
                    key: 'createdAt',
                    label: 'Created',
                    render: (item) =>
                        formatDate(
                            item.createdAt,
                        ),
                },
                {
                    key: 'actions',
                    label: '',
                    render: (item) => (
                        <div className="d-flex gap-2">
                            <NavLink
                                to={`/codes/promo/${item.id}`}
                            >
                                <FaEye />
                            </NavLink>

                            <NavLink
                                to={`/codes/promo/${item.id}`}
                            >
                                <FaEdit />
                            </NavLink>
                        </div>
                    ),
                },
            ];
        },
        [baseFormatters],
    );

    return (
        <Container fluid>
            <Row className="mb-4">
                <Col sm={8}>
                    <span className="fw-semibold">
                        Offer Event Codes
                    </span>
                </Col>
            </Row>

            <Card className="p-3 shadow-sm">
                <Row>
                    <Col md={10}>
                        <SmartTopTool
                            searchType={
                                SEARCH_TYPE
                            }
                            searchConditions={
                                searchConditions
                            }
                            setSearchConditions={
                                handleSetSearchConditions
                            }
                            dateOptions={
                                DATE_OPTIONS
                            }
                            filterOptions={
                                FILTER_OPTIONS
                            }
                            filters={
                                filters
                            }
                            setFilters={
                                handleSetFilters
                            }
                        />
                    </Col>

                    <Col
                        md={2}
                        className="text-end"
                    >
                        <NavLink
                            to="/codes/promo/new"
                            className="btn btn-success"
                        >
                            + New Code
                        </NavLink>
                    </Col>

                    <Col md={12}>
                        <SmartTable
                            data={rows}
                            columns={columns}
                            loading={
                                loading
                            }
                            emptyState={
                                <div className="text-center py-5 text-muted">
                                    {error ||
                                        'No offer codes found'}
                                </div>
                            }
                        />
                    </Col>

                    <Col
                        md={12}
                        className="d-flex justify-content-end"
                    >
                        <SmartPagination
                            page={page}
                            setPage={
                                setPage
                            }
                        />
                    </Col>
                </Row>
            </Card>
        </Container>
    );
};

export default PromoCodesPage;